<template>
  <help-dialog title="Domain Model" maxWidth="800px">
    <template #default>
      <h3 class="mb-2 text-black">
        <v-icon>mdi-alpha-d-box</v-icon>&nbsp;Domain Model Overview
      </h3>
      <p class="text-grey-darken-3">
        The Domain Model is a key artifact in Domain-Driven Design (DDD) and is used
        to create a shared understanding between domain experts and developers.
        Examples of when to use the Domain Model include:
      </p>
      <br/>
      <ul class="pl-10">
        <li class="text-grey-darken-3">API design</li>
        <li class="text-grey-darken-3">System modernization or microservice design</li>
        <li class="text-grey-darken-3">Breaking down monolithic systems</li>
        <li class="text-grey-darken-3">Code generation</li>
      </ul>
      <br />
      <p class="text-grey-darken-4">
        For a full walkthrough of how to create a Domain Model with Qlerify, please
        <a
          href="https://www.qlerify.com/domain-driven-design"
          target="_blank"
          class="text-blue-accent-4"
        >
          click here
        </a>.
      </p>
      <br/><br/>
      <!-- Commands are grouped by aggregate. You can also see the
      command parameters and the associated entities. You can further group entities in Bounded
      Contexts and even generate the code code skeleton for a Bounded Context (a Service API). -->

      <h3 class="mb-2 text-black">
        <v-icon icon="mdi-cog"></v-icon>&nbsp;Configuration
      </h3>
      <p class="text-grey-darken-3">
        First, make sure to have a card type set as ‘Command’
        one set as ‘Aggregate Root’ and one set as 'Read Model' in workflow settings (the
        <v-icon size="x-small">mdi-cog</v-icon> icon top right).
      </p>

      <br/><br/>

      <h3 class="mb-2 text-black">
        Add Cards to Events
      </h3>
      <p class="text-grey-darken-3">
        Now make sure to add one <b>Command</b> and one <b>Aggregate Root</b> to each <b>Event</b>
        (by adding cards of the corresponding types).
      </p>
      <br/>
      <ul class="pl-10">
        <li class="text-grey-darken-3"><b>Note:</b> Add only add <b>one Command</b> and <b>one Root Entity</b> per Event. Additional cards
          will be ignored.
        </li>
        <li class="text-grey-darken-3">
          <b>Hint:</b> When modeling, consider each Event as the result of one Command invoked on one Aggregate.
          If an Event, for example <b>Created order</b>, actually is the result of two separate Commands,
          such as <b>1. Created customer</b> and <b>2. Created Order</b>, then split up this step into two steps.
        </li>
      </ul>

      <br/><br/>

      <h3 class="mb-2 text-black">
        View Grouped Commands
      </h3>
      <p class="text-grey-darken-3">
        Now you can view Commands and Events grouped by their Aggregate Roots.
        The names and descriptions can be updated directly in the diagram.
        Commands and events belong together if they are positioned
        on the same level horizontally.
      </p>

      <br/><br/>

      <h3 class="mb-2 text-black">
        Data Fields
      </h3>
      <p class="text-grey-darken-3">
        <b>Commands</b> and <b>Read Models</b>, along with <b>Entities</b>, can also contain data fields or attributes. These attributes can be linked to related entities.
      </p>
      <br/>
      <p class="text-grey-darken-3">
        If you prefer a more compact view—for example, to better visualize <b>Bounded Contexts</b>—you can hide attributes by deselecting the <b>Show data fields</b> checkbox.
      </p>
      <br/>
      <p class="text-grey-darken-3">
        To view data fields under an <b>Aggregate Root</b>, you must first select an <b>Aggregate Root Entity</b> by clicking the yellow box and choosing an entity (you may need to create entities before you can select one).
      </p>
      <br/>

      <p class="text-grey-darken-3">
        After selecting an entity as the <b>Aggregate Root</b>, you can edit it using the pen icon at the bottom left of the yellow box. The following actions are available for each field on the <b>Entity</b>:
      </p>

      <ul class="ml-5">
        <li>
          <b>Select entity:</b>&nbsp;
          Establish a relationship between a field and another entity. This action is available
          only for fields that are set as <b>Reference</b>.
        </li>
        <li>
          <b>Set as primary key:</b>&nbsp;Marks the field with a key icon (🔑), designating it as the primary key
          for that entity. Only one field may be set as the primary key at a time.
        </li>
        <li><b>Set as reference:</b>&nbsp;Indicates that the field refers to another entity.
        </li>
        <li>
          <b>Set as required/optional:</b>&nbsp;Toggles whether the field is required or not.
        </li>
        <li><b>Delete:</b>&nbsp;Removes the field from the list.</li>
      </ul>
      <br />

      <p class="text-grey-darken-3">
        The following actions are available for each field on the <b>Command</b>:
      </p>
      <ul class="ml-5">
        <li>
          <b>Select entity:</b>&nbsp;
          Establish a relationship between a field and an <b>Entity</b>. This usually mirrors a relationship
          on the <b>Entity</b> that is set as the <b>Aggregate Root</b>.
        </li>
        <li>
          <b>Show/Hide in form:</b>&nbsp;
          Sets whether the field will be shown in the UI form mockup under the sidebar's <b>Data Fields</b> tab.
        </li>
        <li><b>Delete:</b>&nbsp;Removes the field from the list.</li>
      </ul>
      <br />

      <p class="text-grey-darken-3">
        The following actions are available for each field on the <b>Read Model</b>:
      </p>
      <ul class="ml-5">
        <li>
          <b>Add filter:</b>&nbsp;
          Marks the field as indexed for filtering or searching.
        </li>
        <li>
          <b>Select entity:</b>&nbsp;
          Establish a relationship between the field and and <b>Entity</b>.
        </li>
        <li><b>Delete:</b>&nbsp;Removes the field from the list.</li>
      </ul>
      <br /><br />

      <h3 class="mb-2 text-black">
        Bounded Contexts
      </h3>
      <p class="text-grey-darken-3">
        The next step is to assign one or several <b>Entities</b> to one or many <b>Bounded contexts</b>. This is done by clicking
        the button <b>Select bounded context</b> on the entity. Related entities can belong to the same bounded
        context as the <b>Aggregate Root</b> or to another bounded context. If a related entity belongs to another bounded
        context, a label with the text <b>Integration with other bounded contexts</b> indicates that we have
        an integration point.
      </p>

      <br/><br/>

      <h3 class="mb-2 text-black">
        Service API / Code Generation
      </h3>
      <p class="text-grey-darken-3">
        Code can be generated for a <b>Bounded Context</b>, such as a full Service API or an
        OpenAPI/Swagger Specification. You can select from various programming languages,
        databases, and testing frameworks. Additionally, there is an option to generate
        only the OpenAPI Specification. If you wish to use a different technology stack,
        you can manually update the prompt (as a YAML specification) used for AI code generation by
        clicking on 'Customize specification.' For example, you could choose to generate code in
        TypeScript instead of JavaScript. After finalizing the specification, click the
        'Generate API' button. Wait for the generation to complete, then download the source
        code as a ZIP file.
      </p>
      <br/>
      <p class="text-grey-darken-4">
        For a full walkthrough of Code Generation,
        <a
          href="https://www.qlerify.com/ai-generated-code"
          target="_blank"
          class="text-blue-accent-4"
        >
          click here
        </a>.
      </p>
    </template>
  </help-dialog>
</template>

<script>
import HelpDialog from '@/components/HelpDialog.vue'

export default {
  components: {
    HelpDialog
  }
}
</script>
